'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

// ── PostHog for the internal app (app.futalis.group) ─────────────
// Anonymous usage analytics only: which modules and pages are used, on the
// self-hosted instance (NEXT_PUBLIC_POSTHOG_HOST). The users are employees, so
// nothing identifies a person: no identify(), no person profiles, no cookies
// (cookieless_mode "always" — PostHog counts visitors with a daily-rotating
// server-side hash), no autocapture, session replay or heatmaps; URLs lose their
// query string and page titles are dropped (they can name employees or
// customers). Every event carries app = internal.
// Until 2026-10-16 this file identified users by e-mail and ran the shop's
// persona detection on them; both are gone on purpose.

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || '';
const POSTHOG_ENV = process.env.NEXT_PUBLIC_POSTHOG_ENV || 'production';

const stripQuery = (url: unknown) => (typeof url === 'string' ? url.split('?')[0] : url);

/** Init once, on first use — children's effects run before the provider's. */
function ensurePosthog(): boolean {
  if (!POSTHOG_KEY || !POSTHOG_HOST || typeof window === 'undefined') return false;
  if (!posthog.__loaded) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      cookieless_mode: 'always',
      person_profiles: 'never',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: false,
      disable_session_recording: true,
      enable_heatmaps: false,
      disable_surveys: true,
      advanced_disable_flags: true,
      before_send: (event) => {
        if (event?.properties) {
          event.properties.$current_url = stripQuery(event.properties.$current_url);
          event.properties.$referrer = stripQuery(event.properties.$referrer);
          delete event.properties.title;
          delete event.properties.$title;
        }
        return event;
      },
    });
    posthog.register({ app: 'internal', environment: POSTHOG_ENV });
  }
  return true;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    ensurePosthog();
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}

// ── Pageview Tracker ─────────────────────────────────────────────

export function PostHogPageview() {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname || !ensurePosthog()) return;
    posthog.capture('$pageview', {
      $current_url: window.location.origin + pathname,
      $pathname: pathname,
    });
  }, [pathname]);

  return null;
}
